import React, { useEffect, useState } from 'react';
import c from 'classnames';
import { Field } from 'formik';
import { useTranslation } from 'react-i18next';

import GenericFormElement from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/GenericFormElement';

const DATE_TODAY = 'today';

const WebformDate = (props) => {
  const {
    entity,
    values,
    errors,
    touched,
    displayRequiredIndicator,
    setFieldValue,
    setFieldTouched,
  } = props;

  const { t } = useTranslation();

  function parseDate(dateString) {
    // If dateString equals today, return the current date.
    // We reset the hours here to 0, in order to avoid calculation bugs with getTime()
    if (dateString === DATE_TODAY) {
      return new Date(new Date().setHours(0, 0, 0, 0));
    }

    // If dateString is invalid, return undefined.
    if (!dateString || Number.isNaN(new Date(dateString).getTime())) {
      return;
    }

    return new Date(dateString);
  }

  const title = entity.get('#title');
  const key = entity.get('#webform_key');
  const required = entity.get('#required');
  const requiredError = entity.get('#required_error');
  const minDate = parseDate(entity.get('#date_date_min'));
  const maxDate = parseDate(entity.get('#date_date_max'));

  const wrapperClassNames = c('WebformDate', entity.getIn(['#wrapper_attributes', 'class', 0], ''));

  // Set all states for the date.
  const [fields, setFields] = useState({ day: '', month: '', year: '' });

  useEffect(() => {
    // Check if all fields are filled in, if required.
    if (!Object.values(fields).every((value) => value.length > 0)) {
      if (values[key]) {
        setFieldValue(key, '');
      }

      return;
    }

    const date = new Date(`${fields.year}-${fields.month}-${fields.day}`);
    setFieldTouched(key, true);
    setFieldValue(key, date);
  }, [fields, required, key]);

  const handleValidation = (date) => {
    if (required && !date) {
      return requiredError || t(['webform.components.custom.date.required', 'webform.required', 'webform_required']);
    }

    if (!date) {
      return;
    }

    // Depending on your browser, an invalid date overflows to the next month.
    // For example, the 30th of Februari can become the 2nd of March.
    // If that is the case, you still get a valid Date object, but it's not what
    // the user filled in.
    if (
      !(
        date.getDate() === Number(fields.day) &&
        date.getMonth() + 1 === Number(fields.month) &&
        date.getFullYear() === Number(fields.year)
      )
    ) {
      return t(['webform.components.custom.date.invalid', 'webform.invalid', 'webform_invalid']);
    }

    if (minDate && date.getTime() < new Date(minDate).getTime()) {
      return t(['webform.components.custom.date.min', 'webform.min', 'webform_min'], {
        date,
        minDate,
        formatParams: {
          date: { day: 'numeric', month: 'long', year: 'numeric' },
          minDate: { day: 'numeric', month: 'long', year: 'numeric' },
        }
      });
    }

    if (maxDate && date.getTime() > new Date(maxDate).getTime()) {
      return t(['webform.components.custom.date.max', 'webform.max', 'webform_max'], {
        date,
        maxDate,
        formatParams: {
          date: { day: 'numeric', month: 'long', year: 'numeric' },
          maxDate: { day: 'numeric', month: 'long', year: 'numeric' },
        }
      });
    }
  };

  return (
    <GenericFormElement
      className={wrapperClassNames}
      title={title}
      fieldName={key}
      required={required}
      displayRequiredIndicator={displayRequiredIndicator}
      error={errors[key]}
      touched={touched[key]}
    >
      <Field
        id={key}
        name={key}
        validate={handleValidation}
        value={values[key]}
      >
        {() => (
          Object.entries(fields).map(([field, value]) => {
            const fieldName = `${key}_${field}`;
            const placeholder = t(`webform.placeholder_${field}`);

            return (
              <Field
                type="number"
                fieldName={fieldName}
                required={required}
                placeholder={placeholder}
                value={value}
                min="1"
                onChange={(event) => {
                  setFields((oldFields) => ({ ...oldFields, [field]: event.target.value }));
                }}
              />
            );
          })
        )}
      </Field>
    </GenericFormElement>
  );
};

export default WebformDate;
